import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Box, Button, IconButton, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Clear, ExpandMore, Pause, PlayArrow } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { Field } from '@globalvisioninc/ui/field';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { clearRenderLogBuffer, getRenderLogSnapshot, subscribeRenderLogBuffer } from 'pdftron/debug/renderLogBuffer';
import { RenderLogEntry, formatRenderLogDocLabel } from 'pdftron/debug/renderLogConstants';
import useRenderLogging from 'pdftron/debug/useRenderLogging';
import { applyRenderSettingsToViewers, resetRenderSettingsOnViewers } from 'pdftron/debug/applyRenderSettings';
import { RenderLogDocFilter, useRenderLogStore } from 'zstore/renderLogStore';
import {
  RenderSettingKey,
  cachingLevelToMb,
  getRenderSettings,
  mbToCachingLevel,
  useRenderSettingsStore,
} from 'zstore/renderSettingsStore';
import styles from './RenderLogPanelContent.module.css';

const useRenderLogEntries = () =>
  useSyncExternalStore(subscribeRenderLogBuffer, getRenderLogSnapshot, getRenderLogSnapshot);

function filterEntries(entries: readonly RenderLogEntry[], docFilter: RenderLogDocFilter): RenderLogEntry[] {
  if (docFilter === 'ALL') return [...entries];
  return entries.filter((entry) => entry.doc === docFilter);
}

function formatTime(ts: number): string {
  const date = new Date(ts);
  return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: theme.zIndex.modal + 1,
    width: 'min(500px, calc(100vw - 32px))',
    pointerEvents: 'auto',
  },
  pill: {
    cursor: 'pointer',
    padding: theme.spacing(1.125, 1.5),
    backgroundColor: 'var(--gds-color-cool-gray-500)',
    border: '1px solid var(--gds-color-cool-gray-400)',
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[6],
    display: 'inline-flex',
    float: 'right',
    '&:hover': {
      backgroundColor: 'var(--gds-color-cool-gray-450)',
    },
  },
  panel: {
    backgroundColor: 'var(--gds-color-cool-gray-700)',
    border: '1px solid var(--gds-color-cool-gray-450)',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[8],
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'min(560px, 65vh)',
  },
  header: {
    padding: theme.spacing(0, 1.5),
    borderBottom: '1px solid var(--gds-color-cool-gray-450)',
    backgroundColor: 'var(--gds-color-cool-gray-500)',
  },
  panelBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  logControls: {
    padding: theme.spacing(1, 1.5),
    borderBottom: '1px solid var(--gds-color-cool-gray-450)',
    backgroundColor: 'var(--gds-color-cool-gray-500)',
  },
  logRow: {
    padding: theme.spacing(0.25, 0),
    borderBottom: '1px solid var(--gds-color-cool-gray-500)',
    wordBreak: 'break-word',
  },
  docSource: { color: '#7eb6ff' },
  docTarget: { color: '#ffb07e' },
  settingsSection: {
    padding: theme.spacing(1.5),
    borderBottom: '1px solid var(--gds-color-cool-gray-450)',
    backgroundColor: 'var(--gds-color-cool-gray-500)',
  },
}));

type RenderSettingField = {
  key: RenderSettingKey;
  label: string;
  min: number;
  max: number;
  step: number;
  // Optional transforms let a field display a human-friendly unit while the store keeps the
  // engine-facing value (e.g. cache size shown in MB while the store holds PDFTron's level).
  toDisplay?: (storeValue: number) => number;
  fromDisplay?: (displayValue: number) => number;
};

const RENDER_SETTING_FIELDS: Array<RenderSettingField> = [
  {
    key: 'cachingLevel',
    label: 'Cache size (MB)',
    min: 0,
    max: 4000,
    step: 50,
    toDisplay: (level) => Math.round(cachingLevelToMb(level)),
    fromDisplay: mbToCachingLevel,
  },
  { key: 'preRenderLevel', label: 'Pre-render level', min: 0, step: 1, max: 50 },
  { key: 'progressiveTime', label: 'Progressive time (ms)', min: 0, step: 10, max: 5000 },
  { key: 'canvasMultiplier', label: 'Canvas multiplier', min: 0.5, step: 0.25, max: 4 },
];

const RenderLogPanelContent = () => {
  const { classes, cx } = useStyles();
  const collapsed = useRenderLogStore((s) => s.collapsed);
  const paused = useRenderLogStore((s) => s.paused);
  const docFilter = useRenderLogStore((s) => s.docFilter);
  const toggleCollapsed = useRenderLogStore((s) => s.toggleCollapsed);
  const togglePaused = useRenderLogStore((s) => s.togglePaused);
  const setDocFilter = useRenderLogStore((s) => s.setDocFilter);

  const cachingLevel = useRenderSettingsStore((s) => s.cachingLevel);
  const preRenderLevel = useRenderSettingsStore((s) => s.preRenderLevel);
  const progressiveTime = useRenderSettingsStore((s) => s.progressiveTime);
  const canvasMultiplier = useRenderSettingsStore((s) => s.canvasMultiplier);
  const setSetting = useRenderSettingsStore((s) => s.setSetting);
  const resetRenderSettings = useRenderSettingsStore((s) => s.reset);

  useRenderLogging();

  useEffect(() => {
    useRenderSettingsStore.setState(getRenderSettings());
  }, []);

  const renderSettings = useMemo(
    () => ({ cachingLevel, preRenderLevel, progressiveTime, canvasMultiplier }),
    [cachingLevel, preRenderLevel, progressiveTime, canvasMultiplier],
  );

  const handleSettingChange = useCallback(
    (field: RenderSettingField, rawValue: string) => {
      const parsed = Number(rawValue);
      if (Number.isNaN(parsed)) return;
      const clamped = Math.min(Math.max(parsed, field.min), field.max);
      setSetting(field.key, field.fromDisplay ? field.fromDisplay(clamped) : clamped);
    },
    [setSetting],
  );

  const handleApplyRenderSettings = useCallback(() => {
    applyRenderSettingsToViewers(renderSettings);
  }, [renderSettings]);

  const handleResetRenderSettings = useCallback(() => {
    resetRenderSettings();
    resetRenderSettingsOnViewers();
  }, [resetRenderSettings]);

  const allEntries = useRenderLogEntries();
  const filteredEntries = useMemo(() => filterEntries(allEntries, docFilter), [allEntries, docFilter]);

  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const handleListScroll = useCallback(() => {
    const element = listRef.current;
    if (!element) return;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 24;
  }, []);

  useEffect(() => {
    if (!collapsed && stickToBottomRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [filteredEntries, collapsed]);

  if (collapsed) {
    const count = allEntries.length;

    return (
      <Box className={classes.root}>
        <Paper
          className={classes.pill}
          elevation={0}
          onClick={toggleCollapsed}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleCollapsed();
          }}
        >
          <Typography variant="caption" color="common.white" fontWeight={600}>
            Render debug{count > 0 ? ` (${count})` : ''}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.panel} elevation={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" className={classes.header}>
          <Typography variant="subtitle2" color="common.white" fontWeight={700}>
            Render debug
          </Typography>
          <IconButton size="small" onClick={toggleCollapsed} aria-label="Collapse panel" sx={{ color: 'white' }}>
            <ExpandMore fontSize="small" />
          </IconButton>
        </Stack>

        <Box className={classes.panelBody}>
          <Stack spacing={1.5} className={classes.settingsSection}>
            <Typography variant="caption" color="common.white" fontWeight={700}>
              Render settings
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {RENDER_SETTING_FIELDS.map((field) => {
                const { key, label, min, max, step, toDisplay } = field;
                const value = toDisplay ? toDisplay(renderSettings[key]) : renderSettings[key];
                return (
                  <GVTextField
                    key={key}
                    className={styles.field}
                    label={<span className={styles.label}>{label}</span>}
                    type="number"
                    min={min}
                    max={max}
                    step={step}
                    value={String(value)}
                    onValueChange={(value) => handleSettingChange(field, value ?? '')}
                  >
                    <Field.Steppers />
                  </GVTextField>
                );
              })}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyRenderSettings}
                sx={{ fontSize: '11px', textTransform: 'none' }}
              >
                Apply &amp; Refresh
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleResetRenderSettings}
                sx={{ fontSize: '11px', textTransform: 'none', color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between" className={classes.logControls}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                onClick={togglePaused}
                aria-label={paused ? 'Resume logging' : 'Pause logging'}
                sx={{ color: 'white' }}
              >
                {paused ? <PlayArrow fontSize="small" /> : <Pause fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={clearRenderLogBuffer} aria-label="Clear log" sx={{ color: 'white' }}>
                <Clear fontSize="small" />
              </IconButton>
            </Stack>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={docFilter}
              onChange={(_e, value: RenderLogDocFilter | null) => {
                if (value) setDocFilter(value);
              }}
              sx={{ '& .MuiToggleButton-root': { color: 'white', fontSize: '11px', py: 0.25 } }}
            >
              <ToggleButton value="ALL">All</ToggleButton>
              <ToggleButton value="SOURCE">Source</ToggleButton>
              <ToggleButton value="TARGET">New</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Box ref={listRef} className={styles.logList} onScroll={handleListScroll}>
            {filteredEntries.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {paused ? 'Paused — new events are dropped until you resume.' : 'No matching events yet.'}
              </Typography>
            ) : (
              filteredEntries.map((entry) => (
                <Box key={entry.id} className={classes.logRow}>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {formatTime(entry.ts)}{' '}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    className={cx(entry.doc === 'SOURCE' ? classes.docSource : classes.docTarget)}
                    fontWeight={700}
                  >
                    {formatRenderLogDocLabel(entry.doc)}
                  </Typography>
                  <Typography component="span" variant="caption" color="common.white">
                    {' '}
                    {entry.event}
                    {entry.detail ? ` ${entry.detail}` : ''}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default RenderLogPanelContent;
